"use strict";

const _ = require("lodash");

class SelectListControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("select-list-control");
        this._rootElem.tabIndex = 0;
        if (this._model.hasOptionIcons()) {
            this._rootElem.classList.add("icon-options");
        }
        this._rootElem.component = this._model;
        this._selections = [];
        this._commandKeyDown = false;
        this._onClickEvent = this._onClickEvent.bind(this);
        this._onOptionMouseEnter = this._onOptionMouseEnter.bind(this);

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._selections = undefined;
            this._commandKeyDown = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Options", (this._onOptions = this._onOptions.bind(this)));
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("UserSelected", (this._onUserSelected = this._onUserSelected.bind(this)));
        this._rootElem.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._rootElem.addEventListener("blur", (this._onBlurEvent = this._onBlurEvent.bind(this)));
        this._rootElem.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
        this._rootElem.addEventListener(
            "keyup",
            (this._onKeyUpEvent = this._onKeyUpEvent.bind(this))
        );
        this._rootElem.addEventListener(
            "mouseleave",
            (this._onMouseLeaveEvent = this._onMouseLeaveEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Options", this._onOptions);
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("UserSelected", this._onUserSelected);
        this._selections.forEach((selection) => {
            selection.removeEventListener("click", this._onClickEvent);
            selection.removeEventListener("mouseenter", this._onOptionMouseEnter);
        });
        this._rootElem.removeEventListener("focus", this._onFocusEvent);
        this._rootElem.removeEventListener("blur", this._onBlurEvent);
        this._rootElem.removeEventListener("keydown", this._onKeyDownEvent);
        this._rootElem.removeEventListener("keyup", this._onKeyUpEvent);
        this._rootElem.removeEventListener("mouseleave", this._onMouseLeaveEvent);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._setOptions(this._model.options);
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the options have been updated in the model
     * @param newVal {Array}
     * @private
     */
    _onOptions(newVal) {
        this._setOptions(newVal);
    }

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {string|object|boolean|number}
     * @param oldVal {string|object|boolean|number}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when the userSelected value has been updated in the model
     * @param newVal {string|object|boolean|number}
     * @private
     */
    _onUserSelected(newVal) {
        this._setUserSelected(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when an inner button is clicked
     * @param event {Event}
     * @private
     */
    _onClickEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        let newVal = event.currentTarget.value;
        if (this._model.disabled === true || event.currentTarget.hasAttribute("disabled")) {
            return false;
        }
        if (
            (event.ctrlKey === true || this._commandKeyDown === true) &&
            _.isEqual(this._model.value, newVal)
        ) {
            newVal = undefined;
        }
        event.commandKey = this._commandKeyDown;
        this._model.setValue(newVal, true);
        this._model._triggerUserValue(newVal, oldVal, event);
    }

    /**
     * Fired when an option has been hovered over
     * @param event {Event}
     * @private
     */
    _onOptionMouseEnter(event) {
        let optionValue = event.currentTarget.value,
            options = this._model.options;
        options.forEach((option) => {
            if (option.value === optionValue && option.disabled !== true) {
                this._model.userSelected = optionValue;
            }
        });
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = true;
        } else {
            this._commandKeyDown = false;
        }
        this._model._triggerUserKeyDown(event);
    }

    /**
     * Catch the command key
     * @param event {event}
     * @private
     */
    _onKeyUpEvent(event) {
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = false;
        }
    }

    /**
     * Catch the mouse leaving the root element
     * @param event {event}
     * @private
     */
    _onMouseLeaveEvent(/*event*/) {
        this._model.userSelected = undefined;
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the inner buttons with the passed in array of buttons from the model
     * @param newVal {Array}
     * @private
     */
    _setOptions(newVal) {
        this._selections.forEach((selection) => {
            selection.removeEventListener("click", this._onClickEvent);
            selection.removeEventListener("mouseenter", this._onOptionMouseEnter);
        });
        this._selections = [];
        this._rootElem.innerHTML = "";
        newVal.forEach((option) => {
            let selection = document.createElement("list-option");
            selection.innerHTML = option.label;
            selection.value = option.value;
            if (option.disabled === true) {
                selection.setAttribute("disabled", true);
            }
            if (option.description) {
                selection.setAttribute("title", option.description);
            }
            this._setOptionIcon(option.value, selection);
            selection.addEventListener("click", this._onClickEvent);
            selection.addEventListener("mouseenter", this._onOptionMouseEnter);
            this._rootElem.appendChild(selection);
            this._selections.push(selection);
        });
    }

    _setOptionIcon(optionValue, selection) {
        let optionIcon = this._model.getOptionIcon(optionValue);
        let iconEl = document.createElement("base-icon-gl");
        iconEl.dataset.icon = optionIcon;
        selection.insertAdjacentElement("afterbegin", iconEl);
        selection._glIconElem = iconEl;
    }

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._rootElem.setAttribute("disabled", true);
            this._rootElem.tabIndex = -1;
        } else {
            this._rootElem.removeAttribute("disabled");
            this._rootElem.tabIndex = 0;
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("invalid");
        } else {
            this._rootElem.classList.remove("invalid");
        }
    }

    /**
     * Set the inner button that matches the new value active
     * @param newVal {string|object|boolean|number}
     * @private
     */
    _setValue(newVal) {
        this._selections.forEach((selection) => {
            if (_.isEqual(selection.value, newVal)) {
                selection.dataset.active = true;
                selection._glIconElem.dataset.icon = this._model.getSelectedOptionIcon(
                    selection.value
                );
                this._adjustSelectionFocusPosition(selection);
            } else {
                selection.removeAttribute("data-active");
                selection._glIconElem.dataset.icon = this._model.getOptionIcon(selection.value);
            }
        });
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set or remove focus
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /**
     * Set the user selected sate on the option being passed in
     * @param newVal {string|object|boolean|number}
     * @private
     */
    _setUserSelected(newVal) {
        this._selections.forEach((selection) => {
            if (selection.value === newVal) {
                selection.classList.add("user-selected");
                this._adjustSelectionFocusPosition(selection);
            } else {
                selection.classList.remove("user-selected");
            }
        });
    }

    /**
     * Make sure the passed in option DOM element is visible in the list
     * @param selection {element} - Option DOM element
     * @private
     */
    _adjustSelectionFocusPosition(selection) {
        let scrollTop = this._rootElem.scrollTop,
            offsetHeight = this._rootElem.offsetHeight,
            selectionHeight = selection.offsetHeight,
            offsetTop = selection.offsetTop + selectionHeight,
            selectionTop = offsetTop - selectionHeight;
        if (offsetTop > offsetHeight + scrollTop) {
            this._selections.forEach((selection) => {
                selection.removeEventListener("mouseenter", this._onOptionMouseEnter);
            });
            this._rootElem.scrollTop = offsetTop - offsetHeight;
            _.delay(() => {
                this._selections.forEach((selection) => {
                    selection.addEventListener("mouseenter", this._onOptionMouseEnter);
                });
            }, 100);
        } else if (selectionTop < scrollTop) {
            this._selections.forEach((selection) => {
                selection.removeEventListener("mouseenter", this._onOptionMouseEnter);
            });
            this._rootElem.scrollTop = selectionTop;
            _.delay(() => {
                this._selections.forEach((selection) => {
                    selection.addEventListener("mouseenter", this._onOptionMouseEnter);
                });
            }, 100);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = SelectListControlRenderer;
